import { create } from 'zustand';
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
// import { usePresenceStore } from './presenceStore'; // Removed to avoid circular dependency
import { useGlobalStore } from './index';
import { User } from '../types/User';
import UserSyncService from '../services/userSyncService';

interface AuthState {
  // État
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  lastActivity: number;
  
  // Actions
  setUser: (user: User) => void;
  setTokens: (token: string, refreshToken: string) => void;
  updateUser: (updates: Partial<User>) => Promise<void>;
  patchUser: (updates: Partial<User>) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setIsLoggingOut: (isLoggingOut: boolean) => void;
  updateLastActivity: () => void;
  
  // Getters
  getUserRole: () => string | null;
  isDoctor: () => boolean;
  isPatient: () => boolean;
  isAdmin: () => boolean;
  getSessionDuration: () => number;
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector(
    persist(
      immer((set, get) => ({
      // État initial
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isLoggingOut: false,
      lastActivity: Date.now(),

      // Actions
      setUser: (user: User) => set((state) => {
        state.user = user;
        state.isAuthenticated = true;
        state.lastActivity = Date.now();

        // Déclencher la synchronisation post-login en arrière-plan de manière atomique
        setTimeout(() => {
          UserSyncService.getInstance().syncAfterLogin(user).catch(error => {
            console.error('Post-login sync failed:', error);
          });
        }, 0);
      }),

      setTokens: (token: string, refreshToken: string) => set((state) => {
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.lastActivity = Date.now();
      }),

      updateUser: async (updates: Partial<User>) => {
        // Utiliser le service de sync pour mettre à jour avec l'API
        try {
          const updatedUser = await UserSyncService.getInstance().updateUserProfile(updates);
          set((state) => {
            state.user = updatedUser;
            state.lastActivity = Date.now();
          });
        } catch (error) {
          console.error('User update failed:', error);
          throw error;
        }
      },

      patchUser: (updates: Partial<User>) => set((state) => {
        if (state.user) {
          state.user = { ...state.user, ...updates };
          state.lastActivity = Date.now();
        }
      }),

      logout: () => set((state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.isLoggingOut = false;
      }),

      setLoading: (loading: boolean) => set((state) => {
        state.isLoading = loading;
      }),

      setIsLoggingOut: (isLoggingOut: boolean) => set((state) => {
        state.isLoggingOut = isLoggingOut;
      }),

      updateLastActivity: () => set((state) => {
        state.lastActivity = Date.now();
      }),

      // Getters
      getUserRole: () => get().user?.role || null,
      
      isDoctor: () => get().user?.role === 'DOCTOR',
      
      isPatient: () => get().user?.role === 'PATIENT',
      
      isAdmin: () => get().user?.role === 'ADMIN',
      
      getSessionDuration: () => Date.now() - get().lastActivity,
    })),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        lastActivity: state.lastActivity,
      }),
    }
  )
 )
);

// Cleanup function for dependent stores
const cleanupDependentStores = () => {
  // Cleanup global store consultations
  useGlobalStore.getState().consultationActions.setActiveConsultation(null);
  useGlobalStore.getState().consultationActions.setLoading(false);
  useGlobalStore.getState().consultationActions.setError(null);
  // Reset consultations state
  useGlobalStore.setState({
    consultations: {
      activeConsultation: null,
      pendingConsultations: [],
      completedConsultations: [],
      isLoading: false,
      error: null,
    },
  });

  // Cleanup presence store - DÉSACTIVÉ pour éviter référence circulaire
  // TODO: Gérer cette logique dans un composant React qui importe les deux stores
  // usePresenceStore.getState().cleanup();
};

// Add cleanup subscription
useAuthStore.subscribe((state) => {
  if (!state.isAuthenticated && state.user === null) {
    cleanupDependentStores();
  }
});
